import {
  Badge, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, Tooltip,
  makeStyles, tokens,
} from '@fluentui/react-components';
import type {
  RoutingModelChangeSummary, RoutingModelDeployment, RoutingModelSeedStatus,
} from '../../api/configStoreClient';

const useStyles = makeStyles({
  section: {
    marginTop: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    minWidth: 0,
  },
  heading: {
    margin: 0,
  },
  summary: {
    cursor: 'pointer',
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    fontWeight: tokens.fontWeightSemibold,
    borderRadius: tokens.borderRadiusSmall,
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
    },
    '&:hover': { color: tokens.colorBrandForeground1 },
    '& h3': { display: 'inline', marginRight: tokens.spacingHorizontalS },
  },
  badges: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalXS,
  },
  tableViewport: {
    overflow: 'auto',
    maxHeight: '240px',
    maxWidth: '100%',
    marginTop: tokens.spacingVerticalXS,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  table: {
    minWidth: '1320px',
    tableLayout: 'fixed',
    '& th': {
      fontWeight: tokens.fontWeightSemibold,
      position: 'sticky',
      top: 0,
      zIndex: 1,
      backgroundColor: tokens.colorNeutralBackground3,
    },
    '& td': { overflowWrap: 'anywhere', verticalAlign: 'top' },
  },
  muted: {
    color: tokens.colorNeutralForeground2,
  },
  account: {
    cursor: 'help',
    textDecorationLine: 'underline',
    textDecorationStyle: 'dotted',
  },
  list: {
    paddingLeft: tokens.spacingHorizontalXL,
    marginTop: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalXS,
    overflowWrap: 'anywhere',
    '& > li': { marginBottom: tokens.spacingVerticalXS },
  },
  changesViewport: {
    maxHeight: '240px',
    overflow: 'auto',
    padding: tokens.spacingHorizontalS,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

function quotaLabel(value: number): string {
  return value === 0 ? 'Unknown / unmetered' : value.toLocaleString();
}

function Account({ deployment }: { deployment: RoutingModelDeployment }) {
  const styles = useStyles();
  if (!deployment.resourceId) return <span>{deployment.accountName || 'Account not reported'}</span>;
  return (
    <Tooltip content={deployment.resourceId} relationship="description">
      <span tabIndex={0} className={styles.account}>{deployment.accountName || 'Account not reported'}</span>
    </Tooltip>
  );
}

export function RoutingModelDeployments({ status }: { status: RoutingModelSeedStatus }) {
  const styles = useStyles();
  const deployments = status.deployments;
  const detailsAvailable = status.available && deployments
    && (deployments.length > 0 || (status.deploymentCount ?? status.endpointCount) === 0);
  return (
    <section className={styles.section} aria-label="All saved model deployments">
      {!detailsAvailable ? (
        <>
          <Text as="h3" className={styles.heading} weight="semibold" size={200}>All saved model deployments</Text>
          <Text size={200} block className={styles.muted}>
            {status.available ? 'Deployment details unavailable for this saved seed.'
              : 'Saved deployment details unavailable for the current configuration.'}
          </Text>
        </>
      ) : deployments.length === 0 ? (
        <>
          <Text as="h3" className={styles.heading} weight="semibold" size={200}>All saved model deployments</Text>
          <Text size={200} block className={styles.muted}>
            {status.inventoryComplete === true
              ? 'No model deployments. The saved full inventory is an authoritative empty result.'
              : 'No deployments recorded in this partial inventory. Fetch models again to discover all providers and model types.'}
          </Text>
        </>
      ) : (
        <details>
          <summary className={styles.summary}>
            <Text as="h3" className={styles.heading} weight="semibold" size={200}>All saved model deployments</Text>
            <Badge appearance="outline" color="informative">{deployments.length.toLocaleString()}</Badge>
            {status.inventoryComplete !== true && <Badge appearance="outline" color="warning">Partial inventory</Badge>}
          </summary>
          <Text size={200} block className={styles.muted}>
            From the saved seed, not a live Azure query. Focus an account to view its resource ID.
            Routing status shows the eligible chat API or why a deployment is not routed.
          </Text>
          <div className={styles.tableViewport} role="region" aria-label="All saved model deployment table" tabIndex={0}>
            <Table size="small" className={styles.table} aria-label="All saved model deployments">
              <colgroup>
                <col style={{ width: '12%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '5%' }} />
              </colgroup>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell scope="col">Model</TableHeaderCell>
                  <TableHeaderCell scope="col">Deployment alias</TableHeaderCell>
                  <TableHeaderCell scope="col">Account / Region</TableHeaderCell>
                  <TableHeaderCell scope="col">Provider</TableHeaderCell>
                  <TableHeaderCell scope="col">Provisioning state</TableHeaderCell>
                  <TableHeaderCell scope="col">Routing status</TableHeaderCell>
                  <TableHeaderCell scope="col">SKU</TableHeaderCell>
                  <TableHeaderCell scope="col" title="Tokens per minute">TPM</TableHeaderCell>
                  <TableHeaderCell scope="col">Model version</TableHeaderCell>
                  <TableHeaderCell scope="col">Priority</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deployments.map((deployment) => (
                  <TableRow key={deployment.id}>
                    <TableCell><Text weight="semibold" size={200}>{deployment.modelName || 'Not reported'}</Text></TableCell>
                    <TableCell>{deployment.deployment || 'Not reported'}</TableCell>
                    <TableCell>
                      <Account deployment={deployment} />
                      <Text size={200} block className={styles.muted}>{deployment.region || 'Not reported'}</Text>
                    </TableCell>
                    <TableCell>{deployment.modelFormat || 'Not reported'}</TableCell>
                    <TableCell>{deployment.provisioningState || 'Not reported'}</TableCell>
                    <TableCell>{deployment.routingStatus || 'Not reported'}</TableCell>
                    <TableCell>{deployment.sku || 'Not reported'}</TableCell>
                    <TableCell>{quotaLabel(deployment.quotaTPM)}</TableCell>
                    <TableCell>{deployment.modelVersion || 'Not reported'}</TableCell>
                    <TableCell>{deployment.priority}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </details>
      )}
    </section>
  );
}

const changeLabels = {
  modelName: 'Model',
  modelVersion: 'Model version',
  modelFormat: 'Provider',
  provisioningState: 'Provisioning state',
  routingStatus: 'Routing status',
  deployment: 'Deployment alias',
  resourceId: 'Account resource ID',
  accountName: 'Account',
  region: 'Region',
  sku: 'SKU',
  quotaTPM: 'TPM',
  priority: 'Priority',
} satisfies Record<Exclude<keyof RoutingModelDeployment, 'id'>, string>;
type ChangeField = keyof typeof changeLabels;
const changeFields = Object.keys(changeLabels) as ChangeField[];

function changeValue(deployment: RoutingModelDeployment, field: ChangeField): string {
  if (field === 'quotaTPM') return quotaLabel(deployment.quotaTPM);
  const value = deployment[field];
  return typeof value === 'number' ? value.toLocaleString() : value || 'Not reported';
}

function DeploymentIdentity({ deployment }: { deployment: RoutingModelDeployment }) {
  return (
    <>
      <strong>{deployment.deployment || 'Alias not reported'}</strong> — <Account deployment={deployment} /> / {deployment.region || 'Not reported'}
      {' '}— {deployment.modelName || 'Not reported'}
    </>
  );
}

export function RoutingModelChanges({ changes }: { changes?: RoutingModelChangeSummary | null }) {
  const styles = useStyles();
  if (!changes || !changes.comparisonAvailable) {
    return (
      <section className={styles.section} aria-label="Last fetch changes">
        <Text as="h3" className={styles.heading} size={200} weight="semibold">Last fetch changes</Text>
        <Text size={200} block className={styles.muted}>
          {!changes ? 'Change summary unavailable for this response.'
            : 'Comparison unavailable: the previous saved seed could not be compared. Change counts are unknown.'}
        </Text>
      </section>
    );
  }
  const changedCount = changes.added.length + changes.removed.length + changes.updated.length;
  return (
    <section className={styles.section} aria-label="Last successful fetch changes">
      <Text as="h3" className={styles.heading} size={200} weight="semibold">Last fetch changes</Text>
      <div className={styles.badges}>
        <Badge appearance="tint" color="success">Added {changes.added.length.toLocaleString()}</Badge>
        <Badge appearance="tint" color="danger">Removed {changes.removed.length.toLocaleString()}</Badge>
        <Badge appearance="tint" color="informative">Updated {changes.updated.length.toLocaleString()}</Badge>
        <Badge appearance="outline" color="informative">Unchanged {changes.unchangedCount.toLocaleString()}</Badge>
      </div>
      {changedCount === 0 ? (
        <Text size={200} block className={styles.muted}>No deployment metadata changes since the previous saved seed.</Text>
      ) : (
        <details>
          <summary className={styles.summary}>Change details ({changedCount.toLocaleString()})</summary>
          <div className={styles.changesViewport} role="region" aria-label="Deployment change details" tabIndex={0}>
            {([
              ['Added', changes.added],
              ['Removed', changes.removed],
            ] as const).map(([label, deployments]) => deployments.length > 0 && (
              <div key={label}>
                <Text size={200} weight="semibold">{label} ({deployments.length.toLocaleString()})</Text>
                <ul className={styles.list}>
                  {deployments.map((deployment) => (
                    <li key={deployment.id}><DeploymentIdentity deployment={deployment} /></li>
                  ))}
                </ul>
              </div>
            ))}
            {changes.updated.length > 0 && (
              <div>
                <Text size={200} weight="semibold">Updated ({changes.updated.length.toLocaleString()})</Text>
                <ul className={styles.list}>
                  {changes.updated.map((change) => (
                    <li key={change.after.id}>
                      <DeploymentIdentity deployment={change.after} />
                      <ul className={styles.list}>
                        {changeFields.filter((field) => change.changedFields.includes(field)).map((field) => (
                          <li key={field}>
                            {changeLabels[field]}: {changeValue(change.before, field)} → {changeValue(change.after, field)}
                          </li>
                        ))}
                        {change.changedFields.some((field) => !Object.hasOwn(changeLabels, field)) && (
                          <li>Additional deployment metadata changed.</li>
                        )}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </details>
      )}
    </section>
  );
}
